/**
 * Stage 5a — while composer is blocked, poll chat + auto retry-structure on failed.
 * Shared by page chat-matrix and SlidingChat.
 */
import { isAxiosError } from 'axios'
import {
  computed,
  onUnmounted,
  readonly,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { isBackendRoomReady } from '@/modules/chat-matrix-fast/services/chatReadiness'
import type { ChatReadyStatus } from '@/modules/chat-matrix-fast/services/chatReadiness'
import { requestStructureRepair } from '@/modules/chat-matrix-fast/services/matrixRoomPolling'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { chatDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

const POLL_INTERVAL_MS = 2000
const POLL_MAX_ATTEMPTS = 20

type MaybeRefChatId = ComputedRef<number | string | null | undefined>
  | Ref<number | string | null | undefined>

function extractRetryMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const detail = error.response?.data
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim()
    }
    if (detail && typeof detail === 'object') {
      const payload = detail as { detail?: unknown; message?: unknown; error?: unknown }
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail.trim()
      }
      if (typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message.trim()
      }
      if (typeof payload.error === 'string' && payload.error.trim()) {
        return payload.error.trim()
      }
    }
  }

  return 'Не удалось подключиться'
}

export function useChatStructurePoll(options: {
  chatId: MaybeRefChatId
  blocked: ComputedRef<boolean> | Ref<boolean>
  readyStatus: ComputedRef<ChatReadyStatus> | Ref<ChatReadyStatus>
  canCompose: ComputedRef<boolean> | Ref<boolean>
  /** После ready room — опционально переоткрыть UI-чат (page getChat). */
  onRoomReady?: (chatId: number | string) => void | Promise<void>
}) {
  const chatStoreCore = useChatStoreCore()
  let timer: ReturnType<typeof setInterval> | undefined
  const retryNonce = ref(0)
  const failed = ref(false)
  const failureMessage = ref('')

  const clear = () => {
    if (timer) {
      clearInterval(timer)
      timer = undefined
    }
  }

  const resetFailure = () => {
    failed.value = false
    failureMessage.value = ''
  }

  const fail = (message: string) => {
    failed.value = true
    failureMessage.value = message
  }

  const retry = () => {
    resetFailure()
    retryNonce.value += 1
  }

  onUnmounted(clear)

  watch(
    () => ({
      blocked: options.blocked.value,
      chatId: options.chatId.value,
      retryNonce: retryNonce.value,
      status: options.readyStatus.value,
    }),
    ({ blocked, chatId }) => {
      clear()

      if (!blocked || chatId == null || chatId === '') {
        resetFailure()
        return
      }

      let attempts = 0

      const tick = async () => {
        attempts += 1
        const activeId = options.chatId.value
        if (activeId == null || String(activeId) !== String(chatId)) {
          clear()
          return
        }

        if (options.canCompose.value) {
          resetFailure()
          clear()
          return
        }

        let chat = chatStoreCore.currentChat?.id === chatId
          ? chatStoreCore.currentChat
          : chatStoreCore.chats.find(item => item.id === chatId)

        if (attempts > POLL_MAX_ATTEMPTS) {
          clear()
          fail('Не удалось подключиться')
          chatDebugLog('structure poll → stopped (max attempts)', { chatId, attempts })
          return
        }

        try {
          const needsRepair = options.readyStatus.value === 'failed'
            || chat?.transition_status === 'failed'
            || (
              (chat?.transition_status === 'provisioning'
                || options.readyStatus.value === 'creating')
              && !chat?.matrix_room_id?.trim()
            )

          // Stuck provisioning (no room_id) or failed → kick Celery via retry-structure.
          if (needsRepair) {
            const repaired = await requestStructureRepair(chatId)
            if (repaired) {
              chatStoreCore.upsertChatInList(repaired)
              if (chatStoreCore.currentChat?.id === chatId) {
                chatStoreCore.setCurrentChatPreservingMemberCount(repaired)
              }
              chat = repaired
              if (isBackendRoomReady(repaired) && options.canCompose.value) {
                clear()
                await options.onRoomReady?.(chatId)
                return
              }
            }
          }

          // GET /chat/{id}/ only while backend still has no room.
          // joining = Matrix membership; repeating GET does not help and hits ~20 times.
          if (!chat || !isBackendRoomReady(chat)) {
            chat = await ChatApiService.fetchChatById(chatId)
            if (String(options.chatId.value) !== String(chatId)) {
              return
            }

            chatStoreCore.upsertChatInList(chat)
            if (chatStoreCore.currentChat?.id === chatId) {
              chatStoreCore.setCurrentChatPreservingMemberCount(chat)
            }
          }

          if (options.readyStatus.value === 'joining') {
            const topicRoomId = chatStoreCore.activeTopic?.matrix_room_id?.trim()
            const parentRoomId = chat.matrix_room_id?.trim()
            const roomId = topicRoomId || parentRoomId
            if (roomId) {
              const { ensureMatrixChatMembership } = await import(
                '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
              )
              // Topic satellite rooms need Django join; parent/general is usually already joined.
              const forceRepair = Boolean(topicRoomId && topicRoomId !== parentRoomId)
              await ensureMatrixChatMembership({
                chatId,
                roomId,
                chat,
                forceRepair,
              })
            }
          }

          // Backend structure ready ≠ Matrix join. Never getChat while still joining —
          // that nested openChat → openTopic storm and wiped in-flight topic loads.
          if (!options.canCompose.value) {
            return
          }

          resetFailure()
          if (!isBackendRoomReady(chat)) {
            return
          }

          clear()
          await options.onRoomReady?.(chatId)
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 503) {
            clear()
            fail(extractRetryMessage(error))
            return
          }
          // until max attempts
        }
      }

      void tick()
      timer = setInterval(() => {
        void tick()
      }, POLL_INTERVAL_MS)
    },
    { immediate: true },
  )

  return {
    failed: readonly(failed),
    failureMessage: readonly(failureMessage),
    retry,
  }
}

/** Helper for templates that only have activeChat id. */
export function useActiveChatStructurePoll(options: {
  activeChatId: MaybeRefChatId
  blocked: ComputedRef<boolean> | Ref<boolean>
  readyStatus: ComputedRef<ChatReadyStatus> | Ref<ChatReadyStatus>
  canCompose: ComputedRef<boolean> | Ref<boolean>
  onRoomReady?: (chatId: number | string) => void | Promise<void>
}): void {
  useChatStructurePoll({
    chatId: computed(() => options.activeChatId.value),
    blocked: options.blocked,
    readyStatus: options.readyStatus,
    canCompose: options.canCompose,
    onRoomReady: options.onRoomReady,
  })
}
